import { createSelector } from "reselect"
import { get, map, chain, orderBy, filter, isNil, isNull, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { contributorsSummaryTextElem } from "helpers/contributors"
import { numberFormatter } from "helpers/formatters/numbers"
import { getDefaultCurrencyId as getAccountCurrency } from "modules/account-root/selectors"

const getRawConcentrationData = state => get(state, "global.data.securityConcentration.data.model.simulation.composition", {})
const getRawContributors = state => get(state, "global.data.securityConcentrationContributors.data.model.simulation.composition.0")
const getContributorsData = state => get(state, "global.data.securityConcentrationContributors.data.model.simulation.composition.0.positions")
const getOrder = state => get(state, "modules.securityProfileComposition.order", {})
const getCurrencyOverride = (_, props) => get(props, "currency")
const getCurrency = createSelector(
  [getAccountCurrency, getCurrencyOverride],
  (accountCurrency, currencyOverride) => currencyOverride || accountCurrency,
)

// This normally would be a getSelectedField selector but in this case,
// it never changes and you cant actually get the right key from the items(getItems)
const SELECTED_FIELD = "size"

export const getConcentrationData = createSelector(
  [getRawConcentrationData],
  (concentrationData) => chain(concentrationData)
    .map(({ date, positionsCount, concentration: { max, top } }) => ({
      date,
      factors: {
        positionsCount,
        maxConcentration: max / 100,
        topConcentration: top / 100,
      },
    }))
    .filter(data => data)
    .value(),
)

export const hasReferenceEquity = createSelector([getRawConcentrationData],
  (concentrationData) => !isNull(get(concentrationData, "0.referenceEquity", null)),
)

const getContributorsEquity = createSelector(
  [getRawContributors],
  ({ referenceEquity, gmv }) => referenceEquity || gmv,
)

const getContributors = createSelector(
  [getContributorsData, getContributorsEquity],
  (data, equity) => map(data, security => {
    const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)

    return {
      id: security.id,
      country: security.country,
      description: security.description,
      classification: get(security, "classification.name"),
      currency: security.currency,
      assetClass: security.assetClass,
      size: security.economicExposure / 100,
      modelProviderId: security.modelProviderId,
      factors: {
        // This is for the CSV download. The selected item is maxConcentration so it will try to fetch that key as the last column
        // Because we are already displaying the pct equity, we will add its dollar value as the last column
        maxConcentration: numberFormatter(security.economicExposure * equity),
      },
      ticker,
      exchange,
    }
  }))

const getFilteredContributors = createSelector(
  [getContributors],
  (data) => filter(data, security => !isNil(get(security, SELECTED_FIELD))))

export const getSortedContributors = createSelector(
  [getFilteredContributors, getOrder],
  (data, order) => {
    if (data.length === 0) return []

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "economicExposure":
            return security.size
          default:
            return get(security, SELECTED_FIELD)
        }
      },
    ], [order.dir])
  },
)

export const getContributorsSummaryText = createSelector(
  [getSortedContributors, getContributorsEquity, getRawContributors, getCurrency],
  (contributors, equity, rawComposition, currency) => contributorsSummaryTextElem({
    contributors,
    equity,
    ...pick(rawComposition, ["referenceEquity", "gmv"]),
    currency,
  }))
